import { cloneElement, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { MutableRefObject, Ref } from 'react';
import { Tooltip } from 'components/ui/Tooltip';
import type { OverflowTooltipProps } from './OverflowTooltip.props';

const isElementOverflowing = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

const assignRef = (ref: Ref<HTMLElement> | undefined, node: HTMLElement | null) => {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(node);
    return;
  }

  const mutableRef = ref as MutableRefObject<HTMLElement | null>;
  mutableRef.current = node;
};

const OverflowTooltip = ({ children, measureRef, placement = 'top', title = '', ...props }: OverflowTooltipProps) => {
  const childRef = children.props.ref;
  const triggerRef = useRef<HTMLElement | null>(null);
  const childRefRef = useRef<Ref<HTMLElement> | undefined>(childRef);
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
    assignRef(childRefRef.current, node);
    setTriggerElement(currentNode => (currentNode === node ? currentNode : node));
  }, []);

  useLayoutEffect(() => {
    childRefRef.current = childRef;
    assignRef(childRef, triggerRef.current);

    return () => assignRef(childRef, null);
  }, [childRef]);

  // Measure the rendered text node, but allow a larger row/button to own the hover trigger.
  useLayoutEffect(() => {
    const element = measureRef?.current ?? triggerElement;

    if (!element || !title) {
      setIsOverflowing(currentValue => (currentValue ? false : currentValue));
      return undefined;
    }

    const updateOverflowState = () => {
      const nextIsOverflowing = isElementOverflowing(element);
      setIsOverflowing(currentValue => (currentValue === nextIsOverflowing ? currentValue : nextIsOverflowing));
    };

    updateOverflowState();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflowState);
      return () => window.removeEventListener('resize', updateOverflowState);
    }

    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [title, measureRef, triggerElement]);

  const shouldDisableTooltip = !title || !isOverflowing;

  return (
    <Tooltip
      title={shouldDisableTooltip ? '' : title}
      disableFocusListener={shouldDisableTooltip}
      disableHoverListener={shouldDisableTooltip}
      disableTouchListener={shouldDisableTooltip}
      placement={placement}
      {...props}>
      {cloneElement(children, { ref: setTriggerRef })}
    </Tooltip>
  );
};

export default OverflowTooltip;
